import { useEffect, useState } from "react";
import { EntityDetailsDto } from "src/store/features/api/generated";
import { useOnboarding } from "src/store/features/onboarding.slice";

export default function useCompanyFormConfig() {
  const {
    address: { country },
    entityDetails: { companyType, recordType },
  } = useOnboarding();

  const [nameLabel, setNameLabel] = useState("");
  const [idLabel, setIdLabel] = useState("");
  const [addressLabel, setAddressLabel] = useState("");
  const [addressReady, setAddressReady] = useState(false);
  const [enableNatureOfBusiness, setEnableNatureOfBusiness] = useState(false);

  type PageConfig = {
    [K in EntityDetailsDto["companyType"] | EntityDetailsDto["recordType"] as string]: {
      name: string;
      address: string;
      id: string;
      uploadInstructions: { firstLine: string[]; secondLine: string[] };
    };
  };

  const pageConfig: PageConfig = {
    Corporation: {
      name: "Company Name",
      address: "Place of Business/Registered Office Address",
      id: "Company Identification Number e.g., ACN/ABN, EIN",
      uploadInstructions: {
        firstLine: [
          "A notarised or certified as a true copy of the original Articles of Incorporation or Corporate Bylaws, including all pages",
          "If applicable, a notarised or certified as a true copy of the original register or list of shareholders",
        ],
        secondLine: [
          "The full name of the company",
          "The name of each director and shareholder",
          "The country/jurisdiction in which the corporation was established",
          "The governing rules/laws of the company",
        ],
      },
    },
    Other: {
      name: "Company Name",
      address: "Place of Business/Registered Office Address",
      id: "Company Identification Number e.g., ACN/ABN, EIN",
      uploadInstructions: {
        firstLine: [
          "A notarised or certified as a true copy of the original Articles of Incorporation or Corporate Bylaws, including all pages",
          "If applicable, a notarised or certified as a true copy of the original register or list of shareholders",
        ],
        secondLine: [
          "The full name of the company",
          "The name of each director and shareholder",
          "The country/jurisdiction in which the corporation was established",
          "The governing rules/laws of the company",
        ],
      },
    },
    "Limited Liability Company (LLC)": {
      name: "Company Name",
      address: "Place of Business/Registered Office Address",
      id: "Company Identification Number e.g., ACN/ABN, EIN",
      uploadInstructions: {
        firstLine: [
          "A notarised or certified as a true copy of the original Operating Agreement a.k.a. Company Constitution, including all pages",
          "A copy of the Articles of Organisation a.k.a. Certificate of Formation, Articles of Association",
        ],
        secondLine: [
          "The full name of the company",
          "The name of each manager and member (or director and shareholder)",
          "The country/jurisdiction in which the company was established",
          "The governing rules/laws of the company",
        ],
      },
    },
    Partnership: {
      name: "Company Name",
      address: "Place of Business/Registered Office Address",
      id: "Company Identification Number e.g., ACN/ABN, EIN",
      uploadInstructions: {
        firstLine: [
          "A notarised or certified as a true copy of the original Partnership Agreement, including all pages",
          "A copy of the Articles of Organisation a.k.a. Certificate of Formation, Articles of Association",
        ],
        secondLine: [
          "The full name of the partnership",
          "The type of partnership",
          "The name of each partner",
          "The country/jurisdiction in was established",
          "The governing rules/laws of the partnership",
        ],
      },
    },
    "Proprietary Limited Company (Pty Ltd)": {
      name: "Company Name",
      address: "Place of Business/Registered Office Address",
      id: "Company Identification Number e.g., ACN/ABN, EIN",
      uploadInstructions: {
        firstLine: ["A copy of the ASIC Company Extract issued in the last 90 days"],
        secondLine: [
          "The full name and details of the company",
          "The name of each director and shareholder",
        ],
      },
    },
    Trust: {
      name: "Trust Name",
      id: "Trust Identification Number e.g., ABN, EIN",
      address: "Place of Business/Registered Office Address",
      uploadInstructions: {
        firstLine: [
          "A notarised or certified as a true copy of the original Trust Deed a.k.a. Trust Agreement, or Declaration of Trust, including all pages",
        ],
        secondLine: [
          "The full name of the trust",
          "The type of trust",
          "The name of each trustee and beneficiary, or a description of each class of beneficiary",
          "The country/jurisdiction in which the trust was established",
          "The full name of the settlor/grantor of the trust",
          "The governing rules/laws of the trust",
        ],
      },
    },
    SMSF: {
      name: "SMSF Name",
      id: "SMSF Identification Number e.g. ABN, EIN",
      address: "Place of Business/Registered Office Address",
      uploadInstructions: {
        firstLine: [
          "A certified as a true copy of the original SMSF Trust Deed a.k.a. Deed of Establishment or Deed of Variation, including all pages",
        ],
        secondLine: [
          "The full name of the SMSF",
          "The name of each trustee and member",
          "The governing rules/laws of the SMSF",
        ],
      },
    },
    "Unincorporated Association": {
      name: "Association Name",
      id: "Association Identification Number e.g., ACN/ABN, EIN",
      address: "Place of Business/Registered Office Address",
      uploadInstructions: {
        firstLine: [
          "A notarised or certified as a true copy of the original Association Articles a.k.a. Agreement, Charter or Constitution, including all pages",
        ],
        secondLine: [
          "The full name of the association",
          "The type of association",
          "The name of all involved parties, e.g., directors, managers members, trustees",
          "The country/jurisdiction in which the trust was established",
          "The governing rules/laws of the association",
        ],
      },
    },
    "Solo 401(k)": {
      name: "Full Name of 401(k) e.g., ABC Company 401(k) Plan",
      address: "Place of Business/Registered Office Address",
      id: "401(k) Identification Number e.g., Plan Number, EIN",
      uploadInstructions: {
        firstLine: [
          "A notarised or certified as a true copy of the original 401(k) Adoption Agreement, a.k.a. Trust Deed, Plan Document, including all pages",
        ],
        secondLine: [
          "The full name of the 401(k) plan",
          "The type of 401(k)",
          "The name of the Employer a.k.a. Plan Sponsor or Administrator",
          "The name of other involved parties e.g., plan participants, trustees",
          "The governing rules/laws of the 401(k) plan",
        ],
      },
    },
    "Self-Directed IRA": {
      name: "Full Name of IRA",
      id: "IRA Account Number",
      address: "Place of Business/Registered Office Address",
      uploadInstructions: {
        firstLine: [
          "A notarised or certified as a true copy of the original IRA agreement documents, application form, including all pages",
        ],
        secondLine: [
          "The full name of the IRA",
          "The type of IRA",
          "The IRA account owner/participant (note: the IRA must be self-directed)",
          "The IRA custodian and/or trustee",
          "The IRA beneficiary or beneficiaries",
        ],
      },
    },
  };

  const [uploadInstructions, setUploadInstructions] = useState(
    pageConfig[recordType]["uploadInstructions"],
  );

  useEffect(() => {
    setNameLabel(pageConfig[recordType]["name"]);
    setIdLabel(pageConfig[recordType]["id"]);
    setAddressLabel(pageConfig[recordType]["address"]);
    setAddressReady(country !== "");
    setUploadInstructions(pageConfig[recordType]["uploadInstructions"]);
    if (recordType === "Other" && country === "Australia") {
      setUploadInstructions(
        pageConfig["Proprietary Limited Company (Pty Ltd)"]["uploadInstructions"],
      );
    }
    if (recordType === "Other" && companyType) {
      setUploadInstructions(pageConfig[companyType]["uploadInstructions"]);
    }
    setEnableNatureOfBusiness(
      recordType === "Other" || recordType === "Trust",
      // recordType === "Unincorporated Association",
    );
  }, [recordType, country, companyType]);

  return {
    nameLabel,
    idLabel,
    addressLabel,
    uploadInstructions,
    addressReady,
    recordType,
    companyType,
    enableNatureOfBusiness,
  };
}
